/**
 * Минимальное число диалогов в ячейке, ниже которого показатель считается
 * статистически ненадёжным (ячейка приглушается и помечается).
 */
export const RELIABLE_MIN_DIALOGS = 5;

/** Верхний предел подмешивания токен-цвета, чтобы текст ячейки оставался читаемым. */
const MAX_MIX_PERCENT = 70;

/**
 * Показатель степени кривой расцветки (< 1). Распределение целевой доли скошено
 * вниз (почти все ячейки заметно ниже максимума), поэтому линейная шкала красит
 * большинство в красный/нейтраль. Степень < 1 «поднимает» середину шкалы к акценту:
 * нейтральная точка уезжает к низу (~max·0.5^(1/GAMMA) ≈ 3% при max≈20%), красным
 * остаётся только слабое влияние (0–3%), а всё выше плавно тянется к акценту.
 */
const GAMMA = 0.4;

/** Заливка ячейки: цвет темы и его непрозрачность. */
export type HeatmapCellFill = {
  /** Класс фона цветом темы */
  colorClassName: string;
  /** Непрозрачность цвета (0..1) */
  opacity: number;
};

/**
 * Заливка ячейки по шкале [0 … max] целевой доли внутри матрицы (степенная кривая):
 * - слабое влияние (≈0–3%)      → `--color-destructive` (красный);
 * - `targetRate = maxRate`      → `--color-primary` (акцент, максимум);
 * - большинство ячеек           → тянутся к акценту (кривая GAMMA).
 * Цвет привязан к максимуму по матрице, а не к baseline. Только токен-переменные темы.
 * `maxRate` — максимум `targetRate` по надёжным ячейкам матрицы. `null` — ячейка без заливки.
 */
export const getCellFill = (targetRate: number | null, maxRate: number | null): HeatmapCellFill | null => {
  if (targetRate === null || maxRate === null || maxRate <= 0) return null;

  // положение доли на шкале [0..max], подтянутое степенной кривой к акценту
  const raw = Math.min(1, Math.max(0, targetRate / maxRate));
  const t = Math.pow(raw, GAMMA);
  // насыщенность растёт к краям кривой (0 и max), около нейтральной точки — прозрачно
  const intensity = Math.abs(t - 0.5) / 0.5;
  const mixPercent = Math.round(intensity * MAX_MIX_PERCENT);
  if (mixPercent === 0) return null;

  return { colorClassName: t >= 0.5 ? 'bg-primary' : 'bg-destructive', opacity: mixPercent / 100 };
};
